import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, BookOpen, MessageSquare, BarChart3, Shield, Heart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const ParentAboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <img src="/yas-logo.png" alt="YAS Connect" className="w-32 h-32 mx-auto mb-4 object-contain" />
          <h1 className="text-3xl font-black text-white mb-3">Welcome to YAS Connect</h1>
          <p className="text-lg text-white/70">The platform your child uses for their youth development program</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* What is YAS Connect */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-3">What is YAS Connect?</h2>
          <p className="text-gray-600 leading-relaxed">
            YAS Connect is a digital platform used by youth programs to help participants track their growth, complete interactive courses, set and achieve goals, communicate with their mentors, and build career-ready skills. Your child's program uses YAS Connect to make their experience more engaging, structured, and impactful.
          </p>
        </section>

        {/* What your child does on the platform */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-4">What Your Child Does on YAS Connect</h2>
          <div className="grid gap-3">
            {[
              { icon: BookOpen, title: 'Interactive Courses', desc: 'Your child completes engaging courses on topics like financial literacy, communication, leadership, and emotional intelligence. They earn badges and XP as they progress.' },
              { icon: CheckCircle, title: 'Goal Setting & Tracking', desc: 'They set personal and professional goals, track progress weekly, and celebrate achievements. You can see their goal progress from your parent dashboard.' },
              { icon: MessageSquare, title: 'Mentor Communication', desc: 'Your child has a dedicated program mentor they can message directly. This mentor tracks their development and provides guidance throughout the program.' },
              { icon: BarChart3, title: 'Weekly Check-Ins', desc: 'Each week, your child submits a reflection about their experience. This helps their mentor understand how they are doing and where they need support.' },
              { icon: Shield, title: 'Safe & Private', desc: 'All personal reflections and messages between your child and their mentor are private. The platform is monitored by program staff for safety.' },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="flex gap-4 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-gray-900">{item.title}</h3>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Benefits for parents */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-4">How YAS Connect Helps You as a Parent</h2>
          <div className="grid gap-3">
            {[
              { icon: Heart, title: 'Stay Connected to Their Progress', desc: 'See what courses they have completed, goals they have set, and how active they are in the program — all from your parent dashboard.' },
              { icon: MessageSquare, title: 'Communicate with Their Mentor', desc: 'Message your child\'s program mentor directly. Ask questions, share concerns, or get updates on how your child is doing.' },
              { icon: BarChart3, title: 'Understand Their Growth', desc: 'The platform tracks your child\'s interests, career aspirations, skills, and development over time. You can see AI-generated insights about their strengths and areas for growth.' },
              { icon: Shield, title: 'Know They Are Safe', desc: 'All content is moderated by program staff. Your child\'s private reflections stay between them and their mentor.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-4 p-4 rounded-2xl bg-orange-50 border border-orange-100">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-gray-900">{item.title}</h3>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="text-center py-6">
          <p className="text-sm text-gray-500 mb-4">Have questions? Contact your child's program directly or email support@yasconnect.co</p>
          <Button onClick={() => navigate('/parent-how-to')} className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-5">
            Parent Tutorial — How to Use YAS Connect <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ParentAboutPage;
